import React, { useState, useEffect, useRef } from 'react';
import { X, FileText, Zap, CheckCircle, AlertCircle, RefreshCw } from 'lucide-react';
import toast from 'react-hot-toast';
import { apiClient } from '../api'; // Use the centralized API client
import { tokenStorage } from '../utils/tokenStorage';

interface StreamingAnalysisModalProps {
  isOpen: boolean;
  onClose: () => void;
  document: {
    id: number;
    original_name: string;
  } | null;
  projectId: number;
  onAnalysisComplete?: () => void;
}

const StreamingAnalysisModal: React.FC<StreamingAnalysisModalProps> = ({
  isOpen,
  onClose,
  document,
  projectId,
  onAnalysisComplete
}) => {
  const [isStreaming, setIsStreaming] = useState(false);
  const [progress, setProgress] = useState(0);
  const [statusMessage, setStatusMessage] = useState('');
  const [streamingContent, setStreamingContent] = useState('');
  const [isComplete, setIsComplete] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const [connectionStatus, setConnectionStatus] = useState<'disconnected' | 'connecting' | 'connected' | 'error'>('disconnected');
  
  // Refs for cleanup and control
  const contentRef = useRef<HTMLDivElement>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const retryTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const scrollToBottom = () => {
    if (contentRef.current) {
      contentRef.current.scrollTop = contentRef.current.scrollHeight;
    }
  };

  // ✅ FUNCTION TO GET API BASE URL AND TOKEN (using same logic as api.ts)
  const getApiConfig = () => {
    const baseURL = apiClient.defaults.baseURL || process.env.REACT_APP_API_URL || 'http://localhost:5050';
    const token = tokenStorage.getAccessToken();
    
    // Get selected AI model (same as api.ts)
    const getSelectedAIModel = (): string => {
      try {
        const savedLLM = localStorage.getItem('selectedLLM');
        if (savedLLM) {
          const parsed = JSON.parse(savedLLM);
          return parsed.id || 'claude-4-sonnet';
        }
      } catch (error) {
        console.error('Error getting selected AI model:', error);
      }
      return 'claude-4-sonnet';
    };

    return {
      baseURL,
      token,
      selectedModel: getSelectedAIModel()
    };
  };

  // ✅ VERIFY AUTHENTICATION BEFORE STREAMING
  const verifyAuthentication = async (): Promise<boolean> => {
    try {
      // Use the centralized API client to test authentication
      await apiClient.get('/api/auth/me');
      return true;
    } catch (error: any) {
      console.error('Authentication verification failed:', error);
      
      if (error.response?.status === 401) {
        toast.error('Authentication expired. Please log in again.');
        // Trigger logout event (same as api.ts)
        window.dispatchEvent(new CustomEvent('auth:logout'));
        return false;
      }
      
      // For other errors, still try to continue
      console.warn('Auth verification failed but continuing:', error.message);
      return true;
    }
  };

  // ✅ IMPROVED STREAMING WITH PROPER API INTEGRATION
  const startStreamingAnalysis = async (isRetry = false) => {
    if (!document) {
      toast.error('No document selected for analysis');
      return;
    }

    // Clear any existing retry timeout
    if (retryTimeoutRef.current) {
      clearTimeout(retryTimeoutRef.current);
      retryTimeoutRef.current = null;
    }

    // Cancel any existing request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Create new abort controller for this request
    abortControllerRef.current = new AbortController();

    setIsStreaming(true);
    setConnectionStatus('connecting');
    setProgress(isRetry ? 10 : 0);
    setStatusMessage(isRetry ? 'Retrying connection...' : 'Verifying authentication...');
    
    if (!isRetry) {
      setStreamingContent('');
      setIsComplete(false);
      setHasError(false);
      setRetryCount(0);
    }

    try {
      // ✅ STEP 1: VERIFY AUTHENTICATION USING API CLIENT
      const isAuthenticated = await verifyAuthentication();
      if (!isAuthenticated) {
        throw new Error('Authentication failed');
      }

      const { baseURL, token, selectedModel } = getApiConfig();

      if (!token) {
        throw new Error('Authentication token not found. Please log in again.');
      }

      setProgress(20);
      setStatusMessage('Initializing analysis...');



      // ✅ STEP 2: MAKE STREAMING REQUEST WITH SAME HEADERS AS API CLIENT
      const response = await fetch(`${baseURL}/api/analysis/stream`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'Accept': 'text/event-stream',
          'Cache-Control': 'no-cache',
          'X-AI-Model': selectedModel, // Same as api.ts interceptor
        },
        credentials: 'include', // Same as api.ts withCredentials
        body: JSON.stringify({
          projectId: projectId,
          analysisType: 'document',
          documentId: document.id,
          selectedAIModel: selectedModel,
          prompt: `Please provide a comprehensive analysis of the document "${document.original_name}". Focus on key processes, controls, compliance aspects, and provide actionable insights.`
        }),
        signal: abortControllerRef.current.signal
      });

      console.log('📡 Response received:', {
        status: response.status,
        statusText: response.statusText,
        headers: Object.fromEntries(response.headers.entries())
      });

      if (!response.ok) {
        let errorMessage = `Server error (${response.status}): ${response.statusText}`;
        
        try {
          const errorText = await response.text();
          console.error('❌ Error response body:', errorText);
          
          // Try to parse error response
          try {
            const errorData = JSON.parse(errorText);
            errorMessage = errorData.message || errorData.error || errorMessage;
          } catch {
            // If not JSON, use the text as is
            if (errorText.length > 0 && errorText.length < 200) {
              errorMessage = errorText;
            }
          }
        } catch (readError) {
          console.error('❌ Could not read error response:', readError);
        }

        // Handle specific error cases
        if (response.status === 401) {
          // Token might be expired, trigger refresh
          console.log('🔄 Received 401, attempting token refresh...');
          try {
            // Try to refresh token using api client
            await apiClient.get('/api/auth/me');
            
            // If successful, retry the streaming request
            if (retryCount < 2) {
              setRetryCount(prev => prev + 1);
              setTimeout(() => startStreamingAnalysis(true), 1000);
              return;
            }
          } catch (refreshError) {
            errorMessage = 'Authentication failed. Please log in again.';
          }
        }

        throw new Error(errorMessage);
      }

      const reader = response.body?.getReader();
      if (!reader) {
        throw new Error('No response body available for streaming');
      }

      setConnectionStatus('connected');
      setProgress(30);
      setStatusMessage('Analysis stream connected, processing document...');

      const decoder = new TextDecoder();
      let streamingContent = '';
      let lastProgressUpdate = Date.now();

      try {
        while (true) {
          const { done, value } = await reader.read();
          
          if (done) {
            console.log('✅ Streaming completed normally');
            break;
          }

          const chunk = decoder.decode(value, { stream: true });
          const lines = chunk.split('\n');

          for (const line of lines) {
            if (line.startsWith('data: ')) {
              const jsonStr = line.slice(6).trim();
              
              if (jsonStr === '[DONE]') {
                console.log('✅ Received completion signal');
                setProgress(100);
                setStatusMessage('Analysis completed successfully!');
                setIsComplete(true);
                setIsStreaming(false);
                setConnectionStatus('disconnected');
                
                if (onAnalysisComplete) {
                  onAnalysisComplete();
                }
                
                toast.success('Document analysis completed!');
                return;
              }

              if (jsonStr.length > 0) {
                try {
                  const data = JSON.parse(jsonStr);
                  
                  if (data.type === 'content_block_delta' && data.delta?.text) {
                    streamingContent += data.delta.text;
                    setStreamingContent(streamingContent);
                    
                    // Update progress based on content length and time
                    const now = Date.now();
                    if (now - lastProgressUpdate > 1000) { // Update every second
                      const progressIncrement = Math.min(2, 60 / Math.max(1, (streamingContent.length / 100)));
                      setProgress(prev => Math.round(Math.min(95, prev + progressIncrement)));
                      lastProgressUpdate = now;
                    }
                    
                    // Auto-scroll to bottom
                    setTimeout(scrollToBottom, 50);
                  } else if (data.type === 'progress') {
                    setStatusMessage(data.message || '');
                    if (data.progress !== undefined) {
                      setProgress(Math.round(data.progress));
                    }
                  } else if (data.type === 'error') {
                    throw new Error(data.message || 'Streaming analysis failed');
                  }
                } catch (parseError) {
                  console.warn('Failed to parse streaming data:', parseError);
                  // Continue streaming even if we can't parse some chunks
                }
              }
            }
          }
        }

        // If we reach here, streaming ended without completion signal
        if (!isComplete && streamingContent.length > 0) {
          console.log('🔶 Streaming ended without completion signal, but content received');
          setProgress(100);
          setStatusMessage('Analysis completed!');
          setIsComplete(true);
          setIsStreaming(false);
          setConnectionStatus('disconnected');
          
          if (onAnalysisComplete) {
            onAnalysisComplete();
          }
          
          toast.success('Document analysis completed!');
        } else if (!isComplete) {
          throw new Error('Streaming ended without receiving content');
        }

      } catch (streamError) {
        console.error('❌ Streaming error:', streamError);
        throw streamError;
      } finally {
        reader.releaseLock();
      }

    } catch (error: any) {
      console.error('❌ Streaming analysis failed:', error);
      
      setHasError(true);
      setConnectionStatus('error');
      setIsStreaming(false);
      
      let errorMessage = error.message || 'Unknown error occurred';
      
      // Handle specific error types
      if (error.name === 'AbortError') {
        console.log('🛑 Streaming request was cancelled');
        return; // Don't show error for user cancellation
      }
      
      if (errorMessage.includes('Authentication') || errorMessage.includes('401')) {
        setStatusMessage('Authentication failed - please log in again');
        toast.error('Authentication failed. Please log in again.');
        
        // Trigger logout
        setTimeout(() => {
          window.dispatchEvent(new CustomEvent('auth:logout'));
        }, 2000);
      } else if (retryCount < 3 && !errorMessage.includes('fetch')) {
        // Retry for non-network errors
        const retryDelay = Math.min(5000, 1000 * Math.pow(2, retryCount));
        setStatusMessage(`Analysis failed, retrying in ${retryDelay / 1000} seconds...`);
        
        retryTimeoutRef.current = setTimeout(() => {
          setRetryCount(prev => prev + 1);
          startStreamingAnalysis(true);
        }, retryDelay);
        
        return;
      } else {
        setStatusMessage(`Analysis failed: ${errorMessage}`);
        toast.error(`Streaming failed: ${errorMessage}`);
      }
    }
  };

  const handleClose = () => {
    // Cancel any ongoing request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    
    // Clear retry timeout
    if (retryTimeoutRef.current) {
      clearTimeout(retryTimeoutRef.current);
      retryTimeoutRef.current = null;
    }
    
    // Reset state
    setIsStreaming(false);
    setConnectionStatus('disconnected');
    setProgress(0);
    setStatusMessage('');
    setStreamingContent('');
    setIsComplete(false);
    setHasError(false);
    setRetryCount(0);
    
    onClose();
  };

  const handleRetry = () => {
    setRetryCount(0);
    startStreamingAnalysis(false);
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
      }
    };
  }, []);

  if (!isOpen || !document) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-4xl max-h-[90vh] overflow-auto flex flex-col">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-electric-blue bg-opacity-10 rounded-lg">
              <Zap className="w-6 h-6 text-electric-blue" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">
                AI Document Analysis
              </h2>
              <p className="text-sm text-gray-600">{document.original_name}</p>
            </div>
          </div>
          <button
            onClick={handleClose}
            disabled={isStreaming}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors disabled:opacity-50"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Status Section */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center space-x-2">
              {isStreaming && (
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-electric-blue border-t-transparent"></div>
              )}
              {isComplete && <CheckCircle className="w-4 h-4 text-green-500" />}
              {hasError && <AlertCircle className="w-4 h-4 text-red-500" />}
              <span className="text-sm font-medium text-gray-700">
                {statusMessage || 'Ready to start analysis'}
              </span>
              {connectionStatus === 'connected' && (
                <span className="text-xs text-green-600 bg-green-100 px-2 py-1 rounded">
                  Connected
                </span>
              )}
            </div>
            <span className="text-sm text-gray-500">{progress}%</span>
          </div>
          
          {/* Progress Bar */}
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div 
              className={`h-2 rounded-full transition-all duration-300 ${
                hasError ? 'bg-red-500' : isComplete ? 'bg-green-500' : 'bg-electric-blue'
              }`}
              style={{ width: `${progress}%` }}
            ></div>
          </div>
        </div>

        {/* Streaming Content */}
        <div className="flex-1 mb-6 overflow-auto">
          <div className="bg-gray-50 rounded-lg p-4 h-full overflow-y-auto" ref={contentRef}>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center space-x-2">
                <FileText className="w-4 h-4 text-gray-500" />
                <span className="text-sm font-medium text-gray-700">
                  Real-time Analysis Output
                </span>
                {isStreaming && (
                  <div className="flex items-center space-x-1 text-xs text-electric-blue">
                    <div className="w-2 h-2 bg-electric-blue rounded-full animate-pulse"></div>
                    <span>Streaming...</span>
                  </div>
                )}
              </div>
              {retryCount > 0 && (
                <span className="text-xs text-orange-600 bg-orange-100 px-2 py-1 rounded">
                  Retry #{retryCount}
                </span>
              )}
            </div>
            
            <div className="text-sm text-gray-800 leading-relaxed">
              {streamingContent ? (
                <div className="prose prose-sm max-w-none">
                  <div 
                    className="markdown-content whitespace-pre-wrap"
                    style={{ fontFamily: 'ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, "Noto Sans", sans-serif' }}
                  >
                    {streamingContent}
                    {isStreaming && (
                      <span className="inline-block w-2 h-4 bg-electric-blue animate-pulse ml-1"></span>
                    )}
                  </div>
                </div>
              ) : (
                <div className="text-gray-500 italic text-center py-8">
                  <div className="flex flex-col items-center space-y-2">
                    {isStreaming ? (
                      <div className="w-8 h-8 border-2 border-gray-300 border-t-electric-blue rounded-full animate-spin"></div>
                    ) : (
                      <FileText className="w-8 h-8 text-gray-300" />
                    )}
                    <span>
                      {isStreaming 
                        ? 'Connecting to AI analysis service...' 
                        : 'Analysis output will appear here as it\'s generated...'
                      }
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end space-x-3">
          {!isStreaming && !isComplete && !hasError && (
            <button
              onClick={() => startStreamingAnalysis(false)}
              className="bg-electric-blue text-white px-6 py-2 rounded-lg hover:bg-darker-blue transition-colors flex items-center space-x-2"
            >
              <Zap className="w-4 h-4" />
              <span>Start Analysis</span>
            </button>
          )}
          
          {hasError && (
            <button
              onClick={handleRetry}
              className="bg-orange-500 text-white px-6 py-2 rounded-lg hover:bg-orange-600 transition-colors flex items-center space-x-2"
            >
              <RefreshCw className="w-4 h-4" />
              <span>Retry Analysis</span>
            </button>
          )}
          
          {(isComplete || hasError) && (
            <button
              onClick={handleClose}
              className="bg-gray-500 text-white px-6 py-2 rounded-lg hover:bg-gray-600 transition-colors"
            >
              Close
            </button>
          )}
          
          {isStreaming && (
            <button
              onClick={handleClose}
              className="bg-red-500 text-white px-6 py-2 rounded-lg hover:bg-red-600 transition-colors flex items-center space-x-2"
            >
              <X className="w-4 h-4" />
              <span>Cancel</span>
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default StreamingAnalysisModal;